// A tiny store. Views subscribe and redraw what changed.
import type { Model } from './model';
import type { Peaks } from './audio/peaks';
import type { Player } from './audio/player';
import type { Field, Segment } from './parsers/types';
import type { CodecCache } from './ui/codec';

export type Target =
  | { kind: 'seg'; seg: Segment }
  /** A time range the user dragged out on the waveform. */
  | { kind: 'time'; t0: number; t1: number }
  /** A single point under the pointer, in bytes and/or time. */
  | { kind: 'probe'; byte: number | null; t: number | null };

export interface State {
  model: Model | null;
  peaks: Peaks | null;
  player: Player | null;
  decode: 'idle' | 'decoding' | 'ok' | 'error';
  decodeError?: string;
  previewOnly: boolean;
  /** Visible time window, seconds. */
  view: [number, number];
  /** Visible byte-map window in layout units [0, 1]. */
  bview: [number, number];
  hover: Target | null;
  sel: Target | null;
  lane: 'wave' | 'spec' | 'stored';
  /** Progressive codec analysis for the loaded file, if any. */
  codec: CodecCache | null;
  hoverField: Field | null;
  loop: boolean;
  /** Play each frame when stepping with the arrow keys. */
  audition: boolean;
}

type Sub = (s: State, prev: State) => void;

class Store {
  s: State = {
    model: null, peaks: null, player: null, decode: 'idle', previewOnly: false,
    view: [0, 1], bview: [0, 1], hover: null, sel: null, lane: 'wave', codec: null, hoverField: null, loop: false, audition: true,
  };
  private subs = new Set<Sub>();
  set(patch: Partial<State>) {
    const prev = this.s;
    this.s = { ...prev, ...patch };
    this.subs.forEach((f) => f(this.s, prev));
  }
  sub(f: Sub) {
    this.subs.add(f);
    return () => this.subs.delete(f);
  }
}
export const store = new Store();

export const sameTarget = (a: Target | null, b: Target | null) => {
  if (a === b) return true;
  if (!a || !b || a.kind !== b.kind) return false;
  if (a.kind === 'seg' && b.kind === 'seg') return a.seg === b.seg;
  return JSON.stringify(a) === JSON.stringify(b);
};
